'use client';

import { TARGET } from '@decodo/utils';

import { BlockHeader, BlockHeaderContent } from '@/cms/BlockHeaderContentDC';
import { BlockWrapper } from '@/cms/BlockWrapperDC';
import FollowUpBanner from '@/cms/components/FollowUpBanner';
import { BlockComponentModel } from '@/cms/types/blok-component';
import { ScraperMain } from '@/blocks/Scraper/ScraperMain';
import { ScraperTranslationsProvider } from '@/blocks/Scraper/scraperTranslations/index';
import { BannerStrapiProps } from '@/components/atoms/banner/BannerStrapi';
import { normalizeBannerProps } from '@/components/atoms/banner/utils';
import { passButtonIcons } from '@/components/atoms/Button/utils';
import LinkButton from '@/components/atoms/LinkButton';
import { SECTION_NAMES } from '@/integrations/trackingScripts/constants';

import { ScraperProvider } from './state/context';

import iconOnboardingSrc from '~/svg/IconOnboarding.svg';
import { SvgIcon } from '@/components/SvgIcon';

export interface ScraperStrapiProps {
  header: BlockHeader;
  target: TARGET;
  banner?: BannerStrapiProps;
}

export const ScraperStrapi = ({
  block,
}: BlockComponentModel<ScraperStrapiProps>) => {
  const { buttons, ...blockHeader } = block.header ?? {};
  const cta = buttons?.[0];

  return (
    <ScraperTranslationsProvider>
      <BlockWrapper
        data-tracker-section={SECTION_NAMES.SCRAPER_PLAYGROUND}
        classNames={{ inner: 'flex flex-col gap-3' }}
      >
        <BlockHeaderContent
          {...blockHeader}
          buttonPosition='top'
          layoutOptions={{ constrainWidthWithPadding: true }}
          classNames={{
            button: 'flex md:hidden',
          }}
          renderButtons={
            !!cta
              ? [
                  () => (
                    <LinkButton
                      key={cta.url}
                      data-tracker-context='get-trial'
                      fullwidth
                      href={cta.url}
                      variant='filled'
                      color='primary'
                      {...passButtonIcons({
                        ...cta,
                        fallbackIcon: {
                          leftSection: <SvgIcon src={iconOnboardingSrc} asImg />,
                        },
                      })}
                    >
                      {cta.text}
                    </LinkButton>
                  ),
                ]
              : []
          }
          bottomChildren={
            block.banner && (
              <div className='mt-8 md:mt-12'>
                <BlockHeaderContent
                  layoutOptions={{ constrainWidthWithPadding: true }}
                >
                  <FollowUpBanner
                    banner={block.banner && normalizeBannerProps(block.banner)}
                  />
                </BlockHeaderContent>
              </div>
            )
          }
        >
          <ScraperProvider dashboardAction={cta} initialTarget={block.target}>
            <ScraperMain />
          </ScraperProvider>
        </BlockHeaderContent>
      </BlockWrapper>
    </ScraperTranslationsProvider>
  );
};
